import { z } from 'zod';
import { goalSchema, goalWorkoutFuelingTargetsSchema } from './goal';

// ============================================================================
// Persisted Coach Review documents: per-day review notes and the coach's
// proposed plan. Both are owned by the athlete's user and sync via the RxDB↔D1
// replication. Shapes mirror their D1 rows.
// ============================================================================

export const coachReviewNoteAuthorSchema = z.enum(['athlete', 'coach']);
export type CoachReviewNoteAuthor = z.infer<typeof coachReviewNoteAuthorSchema>;

export const reviewNoteDocSchema = z.object({
  id: z.string(),
  userId: z.string(),
  date: z.string(), // YYYY-MM-DD the note is attached to
  author: coachReviewNoteAuthorSchema,
  body: z.string(),
  createdAtUtc: z.string(),
  updatedAtUtc: z.string(),
  deletedAt: z.string().nullable(),
});
export type ReviewNoteDoc = z.infer<typeof reviewNoteDocSchema>;
export type ReviewNoteDocType = ReviewNoteDoc;

export const coachPlanStatusSchema = z.enum([
  'draft',
  'pending_approval',
  'approved',
  'revoked',
]);
export type CoachPlanStatus = z.infer<typeof coachPlanStatusSchema>;

export const coachPlanDayTypeSchema = z.enum([
  'Rest',
  'Recovery',
  'Endurance',
  'Threshold',
  'Long',
  'Race',
]);
export type CoachPlanDayType = z.infer<typeof coachPlanDayTypeSchema>;

// The document `body` is an opaque object in the RxDB schema and refined here,
// mirroring how diaryDays overrides its `body` field. The proposed plan carries
// a goals-page-style goal plus per-intensity workout fuelling targets.
export const coachPlanBodySchema = z.object({
  fromDate: z.string(),
  toDate: z.string(),
  goal: goalSchema,
  workoutFuelingTargets: goalWorkoutFuelingTargetsSchema,
  note: z.string(),
});
export type CoachPlanBody = z.infer<typeof coachPlanBodySchema>;

export const coachPlanDocSchema = z.object({
  id: z.string(),
  userId: z.string(),
  status: coachPlanStatusSchema,
  body: coachPlanBodySchema,
  sentAtUtc: z.string().nullable(),
  createdAtUtc: z.string(),
  updatedAtUtc: z.string(),
  deletedAt: z.string().nullable(),
});
export type CoachPlanDoc = z.infer<typeof coachPlanDocSchema>;
export type CoachPlanDocType = CoachPlanDoc;
